import React from 'react';

import {GwgAccelerator} from '@udemy/career-academies';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {toLocaleDateString} from '@udemy/shared-utils';

import {GoogleRedirectButtons} from './components/google-redirect-buttons.react-component';
import styles from './google-tracker.module.less';

import type {CertificateShareLinks} from './components/google-redirect-buttons.react-component';

function formatCertificateDate(isoDate: string | undefined): string {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return isoDate;
    return toLocaleDateString(d, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    });
}

interface GoogleTrackerProps {
    totalCourses: number;
    completedCourses: number;
    certificateTitle: string;
    certificate?: CertificateShareLinks;
    isAcceleratorEnrolled?: boolean;
    certificateInfo?: GwgAccelerator['certificateInfo'] | null;
}

export const GoogleTracker = ({
    totalCourses,
    completedCourses,
    certificate,
    isAcceleratorEnrolled = true,
    certificateTitle,
    certificateInfo,
}: GoogleTrackerProps) => {
    const {gettext, interpolate} = useI18n();
    const isMdMin = useMatchMedia('md-min');
    const iconSize = isMdMin ? 'xxlarge' : 'xlarge';
    const isCompleted = completedCourses === totalCourses;
    const hasCertificateInfo = !!certificateInfo;

    const progressCountText = interpolate(
        gettext('%(completed)s of %(total)s'),
        {
            completed: completedCourses,
            total: totalCourses,
        },
        true,
    );
    const progressLabelText = gettext('courses completed');
    const enrolledBodyText =
        completedCourses < totalCourses
            ? gettext('Complete all courses in this path to earn the certificate.')
            : interpolate(
                  gettext('Completed on %(date)s'),
                  {date: formatCertificateDate(certificate?.completionDate)},
                  true,
              );
    const preEnrolledBodyText = gettext(
        'After completion, showcase your expertise by adding your Google Certificate to your resume and professional profile.',
    );
    const eyebrowText = hasCertificateInfo
        ? gettext('Earn a certificate')
        : gettext('Build your skills');
    const title = hasCertificateInfo ? certificateTitle : gettext('Complete your learning path');
    const bodyText = hasCertificateInfo
        ? isAcceleratorEnrolled
            ? enrolledBodyText
            : preEnrolledBodyText
        : gettext('Complete all courses in this learning path to improve your career prospects.');

    return (
        <section
            className={styles['google-tracker-card']}
            aria-labelledby="google-certificate-heading"
        >
            <div className={styles['google-tracker-inner']}>
                <div aria-hidden>
                    <Icon glyph="google" size={iconSize} label="Google" />
                </div>

                <div className={styles['google-tracker-copy']}>
                    {!isCompleted && <p className={styles.eyebrow}>{eyebrowText}</p>}
                    <h2 id="google-certificate-heading" className={styles['tracker-heading']}>
                        {title}
                    </h2>
                    <p className={styles['tracker-body-text']}>{bodyText}</p>
                </div>

                {isAcceleratorEnrolled &&
                    (!isCompleted ? (
                        <div className={styles['google-tracker-progress']}>
                            <span className={styles['progress-count']}>{progressCountText}</span>
                            <span className={styles['progress-label']}>{progressLabelText}</span>
                        </div>
                    ) : (
                        <GoogleRedirectButtons
                            certificate={{
                                certificateUrl: certificate?.certificateUrl,
                                isReady: certificate?.isReady ?? false,
                            }}
                        />
                    ))}
            </div>
        </section>
    );
};
